/**
 * XFDF Utilities
 *
 * Single source of truth for all XFDF annotation string operations.
 *
 * Two XFDF formats are used in this app:
 *
 * **Standard format** (database / broadcast):
 * ```xml
 * <xfdf><annots><ink name="abc" .../></annots></xfdf>
 * ```
 *
 * **Command format** (WebViewer's `importAnnotationCommand`):
 * ```xml
 * <xfdf><add><ink name="abc" .../></add><modify/><delete/></xfdf>
 * ```
 */

// ============================================================================
// Constants
// ============================================================================

/** Empty XFDF document — used to suppress embedded PDF annotations in the viewer. */
export const EMPTY_XFDF =
  '<?xml version="1.0" encoding="UTF-8"?><xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"><annots/></xfdf>';

/**
 * Prefix/suffix for wrapping a single annotation element in a complete XFDF document.
 * Used when building per-annotation XFDF strings.
 */
export const XFDF_WRAPPER_START =
  '<?xml version="1.0" encoding="UTF-8"?><xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"><annots>';
export const XFDF_WRAPPER_END = "</annots></xfdf>";

/** A blank XFDF document skeleton for DOM-based construction. */
const XFDF_EMPTY_DOCUMENT =
  '<?xml version="1.0" encoding="UTF-8"?><xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve"><annots></annots></xfdf>';

// ============================================================================
// Format detection
// ============================================================================

/**
 * Returns true if the XFDF string uses the standard `<annots>` format
 * (non-empty — at least one child element).
 */
export const isStandardXfdfFormat = (xfdf: string): boolean => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xfdf, "text/xml");
    if (xmlDoc.querySelector("parsererror")) return false;
    const annotsElement = xmlDoc.querySelector("annots");
    return annotsElement !== null && annotsElement.children.length > 0;
  } catch {
    return false;
  }
};

/**
 * Returns true if the XFDF string is in WebViewer's command format
 * (`<add>`, `<modify>`, or `<delete>` with at least one child).
 */
export const isCommandXfdfFormat = (xfdf: string): boolean => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xfdf, "text/xml");
    if (xmlDoc.querySelector("parsererror")) return false;
    const add = xmlDoc.querySelector("add");
    const modify = xmlDoc.querySelector("modify");
    const del = xmlDoc.querySelector("delete");
    return (
      (add !== null && add.children.length > 0) ||
      (modify !== null && modify.children.length > 0) ||
      (del !== null && del.children.length > 0)
    );
  } catch {
    return false;
  }
};

// ============================================================================
// ID extraction
// ============================================================================

/**
 * Extracts the `name` attribute of the **first** annotation element in a
 * standard-format XFDF string.
 *
 * Handles both `string` and `string | undefined` inputs.
 */
export const extractAnnotationId = (xfdf: string | undefined): string | null => {
  if (!xfdf) return null;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xfdf, "text/xml");
    if (doc.querySelector("parsererror")) return null;
    const annotsElement = doc.querySelector("annots");
    if (!annotsElement?.firstElementChild) return null;
    return annotsElement.firstElementChild.getAttribute("name");
  } catch {
    return null;
  }
};

/**
 * Extracts the `name` attributes of **all** annotation elements in a
 * standard-format XFDF string.
 */
export const extractAllAnnotationIds = (xfdf: string | undefined): string[] => {
  if (!xfdf) return [];
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xfdf, "text/xml");
    if (doc.querySelector("parsererror")) return [];
    const annotsElement = doc.querySelector("annots");
    if (!annotsElement) return [];
    const ids: string[] = [];
    for (const child of annotsElement.children) {
      const id = child.getAttribute("name");
      if (id) ids.push(id);
    }
    return ids;
  } catch {
    return [];
  }
};

// ============================================================================
// Transformation
// ============================================================================

/**
 * Converts a standard-format XFDF string into WebViewer's command format
 * for use with `importAnnotationCommand()`.
 *
 * @param xfdf   Standard-format XFDF (`<annots>` wrapper)
 * @param action The command action (`"add"`, `"modify"`, or `"delete"`)
 * @returns Command-format XFDF string, or `null` on failure.
 */
export const wrapXfdfInCommand = (
  xfdf: string,
  action: "add" | "modify" | "delete",
): string | null => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xfdf, "text/xml");

    if (xmlDoc.querySelector("parsererror")) {
      console.error("[xfdf] Failed to parse XFDF for command wrapping");
      return null;
    }

    const annotsElement = xmlDoc.querySelector("annots");
    if (!annotsElement || annotsElement.children.length === 0) {
      console.error("[xfdf] No annotation elements found in <annots>");
      return null;
    }

    const serializer = new XMLSerializer();
    let annotStrings = "";
    for (const child of Array.from(annotsElement.children)) {
      annotStrings += serializer.serializeToString(child);
    }

    let commandXfdf = '<?xml version="1.0" encoding="UTF-8" ?>';
    commandXfdf += '<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">';
    commandXfdf += "<fields />";

    if (action === "add") {
      commandXfdf += `<add>${annotStrings}</add><modify /><delete />`;
    } else if (action === "modify") {
      commandXfdf += `<add /><modify>${annotStrings}</modify><delete />`;
    } else {
      commandXfdf += `<add /><modify /><delete>${annotStrings}</delete>`;
    }

    commandXfdf += "</xfdf>";
    return commandXfdf;
  } catch (error) {
    console.error("[xfdf] Error wrapping XFDF in command:", error);
    return null;
  }
};

/**
 * Merges an array of per-annotation XFDF strings into a single XFDF document.
 *
 * Each item must have a `xfdf` string field (optional — items without XFDF are
 * silently skipped). The result is suitable for passing to WebViewer's
 * `importAnnotations()`.
 *
 * Returns `undefined` when no valid annotation elements are found.
 */
export const mergeAnnotationsToXfdf = (
  annotations: ReadonlyArray<{ xfdf?: string | null | undefined; id: string }>,
): string | undefined => {
  if (!annotations || annotations.length === 0) return undefined;

  try {
    const parser = new DOMParser();
    const serializer = new XMLSerializer();
    const allAnnotationNodes: Element[] = [];

    for (const annotation of annotations) {
      if (!annotation.xfdf) continue;

      try {
        const xmlDoc = parser.parseFromString(annotation.xfdf, "text/xml");
        if (xmlDoc.querySelector("parsererror")) {
          console.error("[xfdf] Failed to parse XFDF for annotation:", annotation.id);
          continue;
        }

        const annotsElement = xmlDoc.querySelector("annots");
        if (annotsElement && annotsElement.children.length > 0) {
          for (const child of Array.from(annotsElement.children)) {
            allAnnotationNodes.push(child);
          }
        }
      } catch (error) {
        console.error("[xfdf] Error parsing XFDF for annotation:", annotation.id, error);
      }
    }

    if (allAnnotationNodes.length === 0) return undefined;

    const mergedDoc = parser.parseFromString(XFDF_EMPTY_DOCUMENT, "text/xml");
    const annotsElement = mergedDoc.querySelector("annots");
    if (!annotsElement) {
      console.error("[xfdf] Failed to create merged XFDF structure");
      return undefined;
    }

    for (const node of allAnnotationNodes) {
      annotsElement.appendChild(mergedDoc.importNode(node, true));
    }

    return serializer.serializeToString(mergedDoc);
  } catch (error) {
    console.error("[xfdf] Error merging XFDF annotations:", error);
    return undefined;
  }
};

/**
 * Parses an XFDF string once and returns all annotation elements indexed by
 * their `name` attribute.
 *
 * For a single-element lookup, use `extractAnnotationElements(xfdf).get(name) ?? null`.
 *
 * Returns an empty `Map` when the XFDF is malformed or contains no elements.
 */
export const extractAnnotationElements = (xfdf: string): Map<string, Element> => {
  const result = new Map<string, Element>();
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xfdf, "text/xml");
    if (xmlDoc.querySelector("parsererror")) return result;
    const annotsElement = xmlDoc.querySelector("annots");
    if (!annotsElement) return result;
    for (const child of annotsElement.children) {
      const name = child.getAttribute("name");
      if (name) result.set(name, child);
    }
  } catch {
    // return empty map for unparseable XFDF
  }
  return result;
};

/**
 * Wraps a single annotation `Element` in a complete, well-formed XFDF document
 * string (standard format).
 *
 * Useful when the backend expects one XFDF string per annotation.
 */
export const buildSingleAnnotationXfdf = (element: Element): string => {
  const parser = new DOMParser();
  const serializer = new XMLSerializer();
  const doc = parser.parseFromString(XFDF_EMPTY_DOCUMENT, "text/xml");
  const annotsElement = doc.querySelector("annots");
  if (annotsElement) {
    annotsElement.appendChild(doc.importNode(element, true));
  }
  return serializer.serializeToString(doc);
};

/** Applies local upserts and deletions to persisted XFDF by WebViewer annotation name. */
export const applyLocalAnnotationOverlay = (
  baseXfdf: string | undefined,
  overlay: ReadonlyMap<string, string | null>,
): string | undefined => {
  const parser = new DOMParser();
  const document = parser.parseFromString(baseXfdf || XFDF_EMPTY_DOCUMENT, "text/xml");
  if (document.querySelector("parsererror")) return undefined;

  const annotsElement = document.querySelector("annots");
  if (!annotsElement) return undefined;

  for (const annotationElement of Array.from(annotsElement.children)) {
    const name = annotationElement.getAttribute("name");
    if (name && overlay.has(name)) annotationElement.remove();
  }

  for (const [name, xfdf] of overlay) {
    if (!xfdf) continue;

    const annotationElement = extractAnnotationElements(xfdf).get(name);
    if (annotationElement) {
      annotsElement.appendChild(document.importNode(annotationElement, true));
    }
  }

  if (annotsElement.children.length === 0) return undefined;
  return new XMLSerializer().serializeToString(document);
};

/**
 * Replaces just the text content of a single-annotation, standard-format XFDF
 * string, leaving every other attribute/element (position, color, page,
 * flags, etc.) untouched.
 *
 * Purpose-built for saving an in-progress comment during page unload: at that
 * point there's no time left for WebViewer's async `exportAnnotations()` to
 * produce a fresh XFDF export, so this patches the new text into the
 * annotation's last known *persisted* XFDF (e.g. from `useAnnotationHandlers`'
 * already-loaded `annotations`/`pendingAnnotations`) instead. Sending that
 * last-known XFDF as-is with only `<contents>` swapped avoids the backend's
 * wholesale-replace `update` accidentally blanking out the annotation's
 * rect/color/page when only the comment text actually changed.
 *
 * `<contents-richtext>`, if present, is replaced with a single paragraph
 * (Apryse's own convention for a plain-text comment — see e.g. the note
 * editor's line-splitting behavior) so the note renders the new text
 * consistently on reload; any richer formatting from before is intentionally
 * dropped, since there's no time to reconstruct it from a plain string during
 * unload.
 *
 * Returns `null` when `xfdf` doesn't parse or contains no annotation element.
 */
export const patchXfdfContents = (xfdf: string, contents: string): string | null => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xfdf, "text/xml");
    if (xmlDoc.querySelector("parsererror")) return null;

    const annotsElement = xmlDoc.querySelector("annots");
    const annotationElement = annotsElement?.firstElementChild;
    if (!annotationElement) return null;

    // Use the annotation element's own namespace (rather than `createElement`,
    // which creates a null-namespace node and would otherwise serialize as
    // `xmlns=""` on the new element) so a freshly-created `<contents>` matches
    // the rest of the document.
    const ns = annotationElement.namespaceURI;
    const createElement = (tagName: string) =>
      ns ? xmlDoc.createElementNS(ns, tagName) : xmlDoc.createElement(tagName);

    let contentsElement = Array.from(annotationElement.children).find(
      (child) => child.localName.toLowerCase() === "contents",
    );
    if (!contentsElement) {
      contentsElement = createElement("contents");
      annotationElement.appendChild(contentsElement);
    }
    contentsElement.textContent = contents;

    const richTextElement = Array.from(annotationElement.children).find(
      (child) => child.localName.toLowerCase() === "contents-richtext",
    );
    if (richTextElement) {
      richTextElement.replaceChildren();
      const body = createElement("body");
      const paragraph = createElement("p");
      const span = createElement("span");
      span.textContent = contents;
      paragraph.appendChild(span);
      body.appendChild(paragraph);
      richTextElement.appendChild(body);
    }

    return new XMLSerializer().serializeToString(xmlDoc);
  } catch {
    return null;
  }
};

export interface XfdfAnnotationCommand {
  action: "add" | "modify" | "delete";
  id: string;
  xfdf?: string;
}

export const extractAnnotationCommands = (xfdf: string): XfdfAnnotationCommand[] => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xfdf, "text/xml");
  if (xmlDoc.querySelector("parsererror")) return [];

  const commands: XfdfAnnotationCommand[] = [];

  for (const action of ["add", "modify"] as const) {
    const section = xmlDoc.querySelector(action);
    if (!section) continue;

    for (const element of section.children) {
      const id = element.getAttribute("name");
      if (!id) continue;
      commands.push({ action, id, xfdf: buildSingleAnnotationXfdf(element) });
    }
  }

  const deletedIds = xmlDoc.querySelector("delete");
  if (deletedIds) {
    for (const element of deletedIds.children) {
      const id = element.getAttribute("name") ?? element.textContent?.trim();
      if (id) commands.push({ action: "delete", id });
    }
  }

  return commands;
};

export const stripTextMarkupPreview = (xfdf: string): string => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xfdf, "text/xml");
  if (xmlDoc.querySelector("parsererror")) return xfdf;

  const textMarkupTags = new Set(["highlight", "underline", "squiggly", "strikeout"]);

  for (const annotation of Array.from(xmlDoc.querySelector("annots")?.children ?? [])) {
    if (!textMarkupTags.has(annotation.localName.toLowerCase())) continue;

    for (const child of Array.from(annotation.children)) {
      if (child.localName.toLowerCase() === "contents") child.replaceChildren();
    }

    const customData = Array.from(annotation.children).find(
      (child) => child.localName.toLowerCase() === "trn-custom-data",
    );
    const bytes = customData?.getAttribute("bytes");
    if (!customData || !bytes) continue;

    try {
      const parsed = JSON.parse(bytes) as Record<string, unknown>;
      if (!("trn-annot-preview" in parsed)) continue;
      delete parsed["trn-annot-preview"];

      if (Object.keys(parsed).length === 0) {
        customData.remove();
      } else {
        customData.setAttribute("bytes", JSON.stringify(parsed));
      }
    } catch {}
  }

  return new XMLSerializer().serializeToString(xmlDoc);
};

// ============================================================================
// Annotation queries
// ============================================================================

/**
 * Returns true when the XFDF element represents a **status-only** change
 * (i.e. only `state` / `statemodel` attributes were modified, not actual
 * annotation content).
 */
export const isStateChangeOnly = (xfdfElement: Element): boolean => {
  if (!xfdfElement.hasAttribute("state")) return false;
  const nonStateAttributes = Array.from(xfdfElement.attributes).filter(
    (attr) => !["name", "page", "state", "statemodel"].includes(attr.name.toLowerCase()),
  );
  return nonStateAttributes.length === 0;
};

/**
 * Searches `annotations` for the entry whose XFDF `<annots>` element has a
 * child with `name === annotationName`.
 *
 * Works with any array of objects that have an optional `xfdf` field — no
 * domain-specific `Annotation` type dependency.
 */
export const findAnnotationByName = <T extends { xfdf?: string | null | undefined }>(
  annotations: T[] | undefined,
  annotationName: string,
): T | undefined => {
  if (!annotations) return undefined;

  const parser = new DOMParser();

  for (const annotation of annotations) {
    if (!annotation.xfdf) continue;

    try {
      const xmlDoc = parser.parseFromString(annotation.xfdf, "text/xml");
      if (xmlDoc.querySelector("parsererror")) continue;

      const annotsElement = xmlDoc.querySelector("annots");
      if (!annotsElement) continue;

      const found = Array.from(annotsElement.children).some(
        (child) => child.getAttribute("name") === annotationName,
      );
      if (found) return annotation;
    } catch {
      // silently skip unparseable XFDF
    }
  }

  return undefined;
};

// ============================================================================
// File attachment helpers
// ============================================================================

/**
 * Returns the byte size of the embedded file data inside a `<fileattachment>`
 * XFDF element, or `null` if the XFDF does not contain a file attachment.
 *
 * The value comes from the `length` attribute on the `<data>` child element,
 * which represents the compressed (FlateDecode) byte count — i.e. the size
 * stored and transmitted.
 */
export const getFileAttachmentSizeBytes = (xfdf: string): number | null => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xfdf, "text/xml");
    if (xmlDoc.querySelector("parsererror")) return null;
    const fileAttachment = xmlDoc.querySelector("fileattachment");
    if (!fileAttachment) return null;
    const dataElement = fileAttachment.querySelector("data");
    if (!dataElement) return null;
    const lengthAttr = dataElement.getAttribute("length");
    if (!lengthAttr) return null;
    const size = Number.parseInt(lengthAttr, 10);
    if (Number.isNaN(size)) return null;
    return size;
  } catch {
    return null;
  }
};
